import React, { useState, createContext, useContext, useMemo, ReactElement } from 'react';

interface Store {
  type?: string;
  props?: any;
}

interface ModalContextType {
  showModal: (modalType: string, modalProps?: any) => void;
  closeModal: () => void;
  setError: (errorText: string) => void;
  store: Store;
  setProps: (modalType: string, modalProps?: any) => void;
}

const initialState: ModalContextType = {
  showModal: () => {},
  closeModal: () => {},
  setError: () => {},
  store: {},
  setProps: () => {},
};

const ModalContext = createContext(initialState);
export const useModalContext = (): ModalContextType => useContext(ModalContext);

interface ModalProviderProps {
  children: ReactElement;
}

const ModalProvider = ({ children }: ModalProviderProps): React.ReactElement => {
  const [store, setStore] = useState<Store>({});

  const showModal = (type: string, props: any = {}) => {
    setStore((prevState) => ({
      ...prevState,
      type,
      props,
    }));
  };

  const closeModal = () => {
    setStore((prevState) => ({
      ...prevState,
      type: undefined,
      props: {},
    }));
  };

  const setProps = (type: string, props: any = {}) => {
    if (store.type === type) {
      setStore((prevState) => ({
        ...prevState,
        props: {
          ...prevState.props,
          ...props,
        },
      }));
    }
  };

  const setError = (errorText: string) => {
    setStore((prevState) => ({ ...prevState, props: { ...prevState.props, error: errorText } }));
  };

  const contextValue = useMemo<ModalContextType>(() => ({ store, showModal, closeModal, setError, setProps }), [store]);

  return <ModalContext.Provider value={contextValue}>{children}</ModalContext.Provider>;
};

export default ModalProvider;
